import { User } from './types';
import { getLangKey } from '../../../clients/sidebar/models/Language';
import * as translations from './strings';
import { MentionableTask } from '../../../clients/sidebar/views/CreateTask/create_task/types';
const strings = translations[getLangKey()];
import cx from 'classnames';
import styles from './index.module.css';
import { mentionRegex } from '.';

const localTaskIdRegex = (id: string) =>
  new RegExp(`@\\[([##\\d^\\[]+)\\]\\((${id})\\)`);

const isAnId = (id: string): boolean =>
  !id.match(/\D+/g)?.length && !isNaN(parseInt(id, 10));

const getUser = (id: string, users: User[]): User | undefined =>
  users.find((user: User) => user.id === parseInt(id, 10));

const getTask = (id: string, tasks: MentionableTask[]) =>
  tasks.find((task: MentionableTask) => task.id === parseInt(id, 10));

const getUserMentionTitle = ({
  id,
  projectUser,
  user
}: {
  id: string;
  projectUser?: User;
  user?: User;
}): string => {
  if (projectUser) {
    return `${projectUser.name}: ${projectUser.email}`;
  } else if (user) {
    return strings.noAccess(user.name);
  } else if (isAnId(id)) {
    return strings.noFoundUser;
  }
  return id;
};

const mentionReplacedDescription = (description?: string): string =>
  (description || '').replace(
    mentionRegex,
    (match, p1) => `${p1.slice(0, 2) === '##' ? p1 : `@${p1}`}`
  );

const getTaskMentionTitle = ({
  id,
  task
}: {
  id: string;
  task?: MentionableTask;
}): string => {
  if (task) return mentionReplacedDescription(task.display);
  else if (isAnId(id)) return strings.noFoundTask;
  return id;
};

const escapeRegexSensitiveCharacters = (str: string) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getRemovedUserName = (id: string, description: string) => {
  const escapedId = escapeRegexSensitiveCharacters(id);
  const regexString = `@\\[([^\\[]+)\\]\\(${escapedId}\\)`;
  const idSpecificRegex = new RegExp(regexString, 'g');
  const matches = description.match(idSpecificRegex);
  const match =
    matches &&
    matches.find(
      (match: string) => match.indexOf(id) === match.length - 1 - id.length
    );
  return match?.replace(idSpecificRegex, '$1');
};

const getUserMentionClassName = ({
  projectUser,
  user,
  isComment,
  isPrivate,
  id
}: {
  projectUser?: User;
  user?: User;
  isComment?: boolean;
  isPrivate?: boolean;
  id: string;
}) =>
  cx({
    [styles.mention]: projectUser,
    [styles.notMention]: !projectUser,
    [styles.isPrivateComment]: !projectUser && isComment && isPrivate,
    [styles.isCommentMention]: projectUser && isComment,
    [styles.notOnProject]: !projectUser && user,
    [styles.noUserFound]: !user && isAnId(id)
  });

const isTaskMention = (description: string, id: string) => {
  const escapedId = escapeRegexSensitiveCharacters(id);
  return description.match(localTaskIdRegex(escapedId));
};

/** Pattern for merged-task "Read more" link: [Read more](task:123) - 123 is local_task_id */
export const readMoreTaskRegex = /\[Read more\]\(task:(\d+)\)/g;

export type HighlightSegment =
  | { type: 'text'; content: string }
  | { type: 'read_more'; taskId: number };

const parseMergedDescriptionSegments = (description: string): HighlightSegment[] => {
  if (!description || !readMoreTaskRegex.test(description)) {
    return [{ type: 'text', content: description || '' }];
  }
  readMoreTaskRegex.lastIndex = 0;
  const segments: HighlightSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = readMoreTaskRegex.exec(description)) !== null) {
    segments.push({ type: 'text', content: description.slice(lastIndex, match.index) });
    segments.push({ type: 'read_more', taskId: parseInt(match[1], 10) });
    lastIndex = match.index + match[0].length;
  }
  segments.push({ type: 'text', content: description.slice(lastIndex) });
  return segments;
};

export { parseMergedDescriptionSegments };

export {
  getUser,
  getTask,
  getUserMentionTitle,
  getTaskMentionTitle,
  getRemovedUserName,
  getUserMentionClassName,
  isTaskMention,
  localTaskIdRegex,
  mentionReplacedDescription,
  escapeRegexSensitiveCharacters
};
